import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2, Upload, X, FileText } from "lucide-react";
import { useFileUpload } from "@/hooks/useFileUpload";

const AddHomeItemSheet = ({ open, onOpenChange, onSave }) => {
    const [label, setLabel] = useState("");
    const [category, setCategory] = useState("Utilities");
    const [value, setValue] = useState("");
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);

    const { uploadFile, uploading, capturePhoto, selectFile } = useFileUpload();

    const handlePhotoCapture = async () => {
        const photo = await capturePhoto();
        if (photo) setFile(photo);
    };

    const handleFileSelect = async () => {
        const selected = await selectFile();
        if (selected) setFile(selected);
    };

    const handleSave = async () => {
        if (!label || !category) return;

        setLoading(true);
        try {
            let fileData = null;
            if (file) {
                fileData = await uploadFile(file, "vault-files", "house-vault");
            }

            await onSave({
                label,
                category,
                value,
                fileUrl: fileData?.url,
                filePath: fileData?.path
            });

            // Reset and close
            setLabel("");
            setCategory("Utilities");
            setValue("");
            setFile(null);
            onOpenChange(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="max-h-[85vh] rounded-t-3xl flex flex-col"
                style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
            >
                <SheetHeader className="pb-4 pt-8 shrink-0">
                    <SheetTitle className="text-xl font-bold">Upload Document / Record</SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto overflow-x-hidden space-y-4 pb-4">
                    <div className="space-y-2">
                        <Label>Category</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger className="rounded-xl">
                                <SelectValue placeholder="Select Category" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Utilities">Utilities (Meters, etc.)</SelectItem>
                                <SelectItem value="Plans">House Plans</SelectItem>
                                <SelectItem value="Compliance">Certificates & Compliance</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label>Label</Label>
                        <Input
                            placeholder="e.g. Electricity Meter Number"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            className="rounded-xl"
                        />
                    </div>

                    {category === "Utilities" && (
                        <div className="space-y-2">
                            <Label>Number / Value</Label>
                            <Input
                                placeholder="Enter meter number or value"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                className="rounded-xl"
                            />
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label>Attachment (Photo or PDF)</Label>
                        {!file ? (
                            <Button
                                variant="outline"
                                className="w-full h-24 rounded-2xl flex flex-col gap-2 border-dashed"
                                onClick={handleFileSelect}
                            >
                                <Upload className="w-6 h-6 text-primary" />
                                <span className="text-xs uppercase font-bold tracking-wider">Upload</span>
                            </Button>
                        ) : (
                            <div className="relative p-4 border border-border rounded-xl bg-muted/30 flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <FileText className="w-5 h-5 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{file.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                </div>
                                <button
                                    onClick={() => setFile(null)}
                                    className="p-1 hover:bg-muted rounded-full"
                                >
                                    <X className="w-4 h-4 text-muted-foreground" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="shrink-0 pt-2 border-t border-border mt-auto">
                    <Button
                        className="w-full rounded-xl py-6 text-lg font-bold"
                        onClick={handleSave}
                        disabled={loading || uploading || !label}
                    >
                        {loading || uploading ? (
                            <div className="flex items-center gap-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>{uploading ? "Uploading..." : "Saving..."}</span>
                            </div>
                        ) : (
                            "Save to Home"
                        )}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default AddHomeItemSheet;
